import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { operatorConsole, OperatorSession, SessionsResponse } from '../../lib/api/operatorConsole';
import { Button } from '../../components/ui/Button';

export function SessionsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<SessionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const PER_PAGE = 25;

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await operatorConsole.listSessions({ page, per: PER_PAGE });
        setData(res);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
  }, [page]);

  const handleRowClick = (sessionId: number) => {
    navigate(`/sessions/${sessionId}`);
  };

  const totalPages = data ? Math.ceil(data.meta.total_count / data.meta.per) : 0;
  const canPrev = page > 1;
  const canNext = page < totalPages;

  if (loading) return <div className="p-6">Loading sessions...</div>;
  if (error) return <div className="p-6 text-red-600">Error: {error}</div>;
  if (!data) return <div className="p-6">No data</div>;

  return (
    <div className="space-y-4 p-6">
      <h1 className="text-2xl font-bold">Sessions</h1>

      {data.sessions.length === 0 ? (
        <div className="rounded-lg border border-ui-border bg-ui-card p-6 text-center text-ui-text-secondary">
          No sessions found
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-ui-border">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">ID</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Player</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Template</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Created</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Expires</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.sessions.map((session: OperatorSession) => (
                  <tr
                    key={session.id}
                    onClick={() => handleRowClick(session.id)}
                    className="cursor-pointer border-b border-ui-border hover:bg-ui-bg"
                  >
                    <td className="px-4 py-3 text-sm text-ui-text">{session.id}</td>
                    <td className="px-4 py-3 text-sm text-ui-text">
                      {session.player_username || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-ui-text">{session.template}</td>
                    <td className="px-4 py-3 text-sm text-ui-text-secondary">
                      {new Date(session.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-ui-text-secondary">
                      {new Date(session.expires_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={
                          session.expired || session.deleted_at
                            ? 'text-red-600'
                            : 'text-green-600'
                        }
                      >
                        {session.expired || session.deleted_at ? 'expired' : 'active'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between rounded-lg border border-ui-border bg-ui-card px-4 py-3">
            <div className="text-sm text-ui-text-secondary">
              Page {page} of {totalPages} • Total {data.meta.total_count} sessions
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={!canPrev}
                className="px-3 py-1 text-sm disabled:opacity-50"
              >
                Prev
              </Button>
              <Button
                onClick={() => setPage(p => p + 1)}
                disabled={!canNext}
                className="px-3 py-1 text-sm disabled:opacity-50"
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
